// Config-driven table with per-column, opt-in sort + filter menus (the standard
// data-grid pattern). Filters are hidden behind a column funnel icon; the icon
// shows an active state once applied. Sorting is multi-column and cumulative:
// clicking a column's sort cycles desc → asc → off and appends to an ordered
// sort list. Active sorts AND filters surface as removable chips above the
// table, with "Clear all". Everything is URL-driven:
//   ?sort=value:desc,pl:asc   + one param per active filter.
//
// A column enables sort by providing `sort`, and filter by providing `filter`.

import type { JSX, ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowDown, ArrowUp, ArrowUpDown, Check, ListFilter, X } from "lucide-react";
import { cn } from "../lib/utils.js";
import { Card } from "./ui/card.js";
import { Input } from "./ui/input.js";
import { Popover } from "./ui/popover.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table.js";

export interface ColumnFilterDef<T> {
  kind: "search" | "select";
  /** Value used to match a row against the filter. */
  accessor: (row: T) => string;
  /** Options for a select filter (usually distinct accessor values). */
  options?: (rows: T[]) => string[];
  placeholder?: string;
}

export interface Column<T> {
  /** Unique key — also the sort key and the filter's URL param name. */
  key: string;
  label: string;
  align?: "right";
  cell: (row: T) => ReactNode;
  /** Provide to make the column sortable. */
  sort?: (a: T, b: T) => number;
  /** Provide to make the column filterable. */
  filter?: ColumnFilterDef<T>;
}

type SortDir = "asc" | "desc";
interface SortSpec {
  key: string;
  dir: SortDir;
}

const parseSorts = (raw: string): SortSpec[] =>
  raw
    .split(",")
    .map((tok) => tok.trim())
    .filter(Boolean)
    .map((tok) => {
      const [key, d] = tok.split(":");
      return { key: key ?? "", dir: d === "asc" ? "asc" : "desc" } satisfies SortSpec;
    })
    .filter((s) => s.key !== "");

const serializeSorts = (list: SortSpec[]): string => list.map((s) => `${s.key}:${s.dir}`).join(",");

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  defaultSort,
  loading = false,
  emptyText,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T, i: number) => string;
  /** Column key applied (descending) when the URL carries no explicit sort. */
  defaultSort: string;
  loading?: boolean;
  emptyText: string;
}): JSX.Element {
  const [params, setParams] = useSearchParams();
  const sortableKeys = new Set(columns.filter((c) => c.sort).map((c) => c.key));

  // `sort` absent → default; `sort=""` (present, empty) → explicitly unsorted.
  const rawSort = params.get("sort");
  const sorts: SortSpec[] = (
    rawSort === null ? [{ key: defaultSort, dir: "desc" as const }] : parseSorts(rawSort)
  ).filter((s) => sortableKeys.has(s.key));

  const setParam = (k: string, v: string): void => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next);
  };
  const writeSorts = (list: SortSpec[]): void => {
    const next = new URLSearchParams(params);
    // Empty list → keep an explicit "" so it doesn't fall back to the default.
    next.set("sort", serializeSorts(list));
    setParams(next);
  };
  const cycleSort = (key: string): void => {
    const idx = sorts.findIndex((s) => s.key === key);
    if (idx === -1) writeSorts([...sorts, { key, dir: "desc" }]);
    else if (sorts[idx]?.dir === "desc")
      writeSorts(sorts.map((s, i) => (i === idx ? { key, dir: "asc" } : s)));
    else writeSorts(sorts.filter((_, i) => i !== idx));
  };
  const removeSort = (key: string): void => {
    writeSorts(sorts.filter((s) => s.key !== key));
  };

  const activeFilters = columns
    .filter((c) => c.filter)
    .map((c) => ({ col: c, value: params.get(c.key) ?? "" }))
    .filter((a) => a.value !== "");

  const clearAll = (): void => {
    const next = new URLSearchParams(params);
    for (const a of activeFilters) next.delete(a.col.key);
    next.set("sort", ""); // explicitly unsorted
    setParams(next);
  };

  // Filter, then multi-key sort (both cheap for a client-held portfolio).
  let visible = rows;
  for (const { col, value } of activeFilters) {
    const f = col.filter;
    if (!f) continue;
    const needle = value.toLowerCase();
    visible = visible.filter((r) =>
      f.kind === "search" ? f.accessor(r).toLowerCase().includes(needle) : f.accessor(r) === value,
    );
  }
  if (sorts.length > 0) {
    const cmps = sorts
      .map((s) => {
        const col = columns.find((c) => c.key === s.key);
        return col?.sort ? { cmp: col.sort, sign: s.dir === "asc" ? 1 : -1 } : null;
      })
      .filter((x): x is { cmp: (a: T, b: T) => number; sign: number } => x !== null);
    visible = [...visible].sort((a, b) => {
      for (const { cmp, sign } of cmps) {
        const r = cmp(a, b) * sign;
        if (r !== 0) return r;
      }
      return 0;
    });
  }

  const colLabel = (key: string): string => columns.find((c) => c.key === key)?.label ?? key;
  const hasChips = sorts.length > 0 || activeFilters.length > 0;

  return (
    <>
      {hasChips && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {sorts.map((s, i) => (
            <span
              key={`sort-${s.key}`}
              className="bg-accent text-foreground inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs"
            >
              {sorts.length > 1 && <span className="text-muted-foreground">{i + 1}</span>}
              {s.dir === "asc" ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />}
              <span className="font-medium">{colLabel(s.key)}</span>
              <button
                type="button"
                onClick={() => {
                  removeSort(s.key);
                }}
                className="hover:text-foreground text-muted-foreground -mr-0.5 ml-0.5"
                aria-label={`Remove ${colLabel(s.key)} sort`}
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
          {activeFilters.map(({ col, value }) => (
            <span
              key={`filter-${col.key}`}
              className="bg-secondary text-secondary-foreground inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs"
            >
              <span className="text-muted-foreground">{col.label}:</span>
              <span className="font-medium">{value}</span>
              <button
                type="button"
                onClick={() => {
                  setParam(col.key, "");
                }}
                className="hover:text-foreground text-muted-foreground -mr-0.5 ml-0.5"
                aria-label={`Remove ${col.label} filter`}
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={clearAll}
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs"
          >
            <X className="size-3" /> Clear all
          </button>
        </div>
      )}

      <Card className="overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c) => {
                const sortIdx = sorts.findIndex((s) => s.key === c.key);
                const sortSpec = sortIdx === -1 ? null : sorts[sortIdx];
                const filterActive = c.filter ? (params.get(c.key) ?? "") !== "" : false;
                return (
                  <TableHead key={c.key} className={c.align === "right" ? "text-right" : undefined}>
                    <div
                      className={cn(
                        "inline-flex items-center gap-1",
                        c.align === "right" && "flex-row-reverse",
                      )}
                    >
                      {c.sort ? (
                        <button
                          type="button"
                          onClick={() => {
                            cycleSort(c.key);
                          }}
                          className={cn(
                            "hover:text-foreground inline-flex items-center gap-1 transition-colors",
                            sortSpec ? "text-foreground font-medium" : "text-muted-foreground",
                          )}
                        >
                          {c.label}
                          {sortSpec ? (
                            sortSpec.dir === "asc" ? (
                              <ArrowUp className="size-3.5" />
                            ) : (
                              <ArrowDown className="size-3.5" />
                            )
                          ) : (
                            <ArrowUpDown className="size-3.5 opacity-50" />
                          )}
                          {sortSpec && sorts.length > 1 && (
                            <span className="text-muted-foreground text-[9px] leading-none">
                              {sortIdx + 1}
                            </span>
                          )}
                        </button>
                      ) : (
                        <span>{c.label}</span>
                      )}

                      {c.filter && (
                        <Popover
                          align={c.align === "right" ? "end" : "start"}
                          trigger={({ toggle }) => (
                            <button
                              type="button"
                              onClick={toggle}
                              aria-label={`Filter ${c.label}`}
                              className={cn(
                                "hover:bg-accent rounded p-0.5 transition-colors",
                                filterActive
                                  ? "text-brand bg-accent"
                                  : "text-muted-foreground hover:text-foreground",
                              )}
                            >
                              <ListFilter className="size-3.5" />
                            </button>
                          )}
                        >
                          {(close) => (
                            <FilterMenu
                              col={c}
                              rows={rows}
                              value={params.get(c.key) ?? ""}
                              onChange={(v) => {
                                setParam(c.key, v);
                              }}
                              close={close}
                            />
                          )}
                        </Popover>
                      )}
                    </div>
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((r, i) => (
              <TableRow key={rowKey(r, i)}>
                {columns.map((c) => (
                  <TableCell
                    key={c.key}
                    className={c.align === "right" ? "text-right tabular-nums" : undefined}
                  >
                    {c.cell(r)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {loading && (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-muted-foreground py-8 text-center">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-muted-foreground py-8 text-center">
                  {emptyText}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </>
  );
}

function FilterMenu<T>({
  col,
  rows,
  value,
  onChange,
  close,
}: {
  col: Column<T>;
  rows: T[];
  value: string;
  onChange: (v: string) => void;
  close: () => void;
}): JSX.Element | null {
  const f = col.filter;
  if (!f) return null;

  if (f.kind === "search") {
    return (
      <div className="w-56 p-1">
        <Input
          autoFocus
          type="search"
          defaultValue={value}
          placeholder={f.placeholder ?? `Filter ${col.label}…`}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          className="h-8 text-xs"
        />
      </div>
    );
  }

  const options = f.options ? f.options(rows) : [];
  const rowCls =
    "hover:bg-accent flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm";
  return (
    <div className="max-h-72 w-48 overflow-auto">
      <button
        type="button"
        className={cn(rowCls, value === "" && "font-medium")}
        onClick={() => {
          onChange("");
          close();
        }}
      >
        <span className="w-4 shrink-0">{value === "" && <Check className="size-3.5" />}</span>
        All
      </button>
      {options.map((o) => (
        <button
          key={o}
          type="button"
          className={cn(rowCls, value === o && "font-medium")}
          onClick={() => {
            onChange(o);
            close();
          }}
        >
          <span className="w-4 shrink-0">{value === o && <Check className="size-3.5" />}</span>
          <span className="truncate">{o}</span>
        </button>
      ))}
    </div>
  );
}

/** Distinct, sorted, non-empty values — handy for select-filter options. */
export const uniq = (xs: (string | null)[]): string[] =>
  [...new Set(xs.filter((x): x is string => !!x))].sort((a, b) => a.localeCompare(b));
